import { Container, Rectangle, Sprite, Texture } from "pixi.js";
import type { PresentationTaskRunner, TweenEasing } from "@slot/presentation";
import { CELL, FRAME_ART, ROW_FRAME_ART, art, rowCentreY } from "./layout";

/** The scope the frame's resize runs in: the base game's, outside the round's (no slam reaches it). */
const RESIZE_SCOPE = "coin_hold_row_frame";

/** Where the frame lies: its top in the grid root's space, and the stretch between its halves. */
interface Placement {
  y: number;
  between: number;
}

/**
 * The row frame (`centerRowGridFrame`) over the active lines' rows (decision 56): one frame around them, as 3 Coin
 * Volcanoes draws its active lines. The art frames one row, so it is cut across its window's middle, where only its
 * vertical bars cross: its top half over the first row, a thin slice of the cut stretched over the rows between, its
 * bottom half under the last. Laid out in the grid root's space (`GridContainer` places it there). A change of rows can
 * slide and stretch it there, eased.
 */
export class RowFrameView extends Container {
  private readonly top: Sprite;
  private readonly band: Sprite;
  private readonly bottom: Sprite;
  private readonly topHeight: number;
  /** The grid root's offset from the grid's origin: the frame's rows in the root's space. */
  private readonly rootOffsetY: number;
  private readonly runner: PresentationTaskRunner;
  /** The rows it goes around (or is on its way to), top to bottom. */
  private rows: readonly number[] = [];
  private between = 0;
  /** The resize running (a new span supersedes it). */
  private resizeGeneration = 0;

  public constructor(texture: Texture, rootOffsetY: number, runner: PresentationTaskRunner) {
    super({ label: "RowFrame" });
    this.rootOffsetY = rootOffsetY;
    this.runner = runner;
    // The imported image's pixels per delivery pixel; the cut lands on a whole pixel of the image.
    const k = texture.width / ROW_FRAME_ART.width;
    const cut = Math.round(ROW_FRAME_ART.windowCentreY * k);
    const slice = (y0: number, y1: number): Texture =>
      new Texture({ source: texture.source, frame: new Rectangle(texture.frame.x, texture.frame.y + y0, texture.frame.width, y1 - y0) });
    const width = art(ROW_FRAME_ART.width);
    this.topHeight = art(cut / k);
    this.top = new Sprite({ texture: slice(0, cut), label: "Top" });
    this.top.setSize(width, this.topHeight);
    this.band = new Sprite({ texture: slice(cut, cut + 1), label: "Between" });
    this.band.width = width;
    this.band.y = this.topHeight;
    this.bottom = new Sprite({ texture: slice(cut, texture.frame.height), label: "Bottom" });
    this.bottom.setSize(width, art((texture.frame.height - cut) / k));
    this.x = art(-FRAME_ART.origin.x);
    this.addChild(this.top, this.band, this.bottom);
    this.span([1]);
  }

  /**
   * The frame around `rows` (consecutive, top to bottom): its window centres on theirs. With `motion` it slides and
   * stretches there from where it lies now (a resize under way included), eased; without, it is there at once.
   */
  public span(rows: readonly number[], motion?: { seconds: number; easing: TweenEasing }): void {
    if (rows.length === this.rows.length && rows.every((row, i) => row === this.rows[i])) return;
    this.rows = [...rows];
    this.resizeGeneration += 1;
    const to = this.placementOf(rows);
    if (!motion || motion.seconds <= 0) {
      this.place(to);
      return;
    }
    const generation = this.resizeGeneration;
    const from: Placement = { y: this.y, between: this.between };
    void this.runner.tweenProgress(
      motion.seconds,
      (k) => {
        if (generation !== this.resizeGeneration || this.destroyed) return;
        this.place({ y: from.y + (to.y - from.y) * k, between: from.between + (to.between - from.between) * k });
      },
      RESIZE_SCOPE,
      { easing: motion.easing },
    );
  }

  private placementOf(rows: readonly number[]): Placement {
    const first = rows[0] ?? 1;
    return { y: rowCentreY(first) - this.topHeight - this.rootOffsetY, between: Math.max(0, rows.length - 1) * CELL.pitchY };
  }

  private place(at: Placement): void {
    this.y = at.y;
    this.between = at.between;
    this.band.height = at.between;
    this.band.visible = at.between > 0;
    this.bottom.y = this.topHeight + at.between;
  }
}
